/**
 * Thank-you card shown by the chat after the router stored a lead (SSE event "leadcomplete", or the "completion"
 * object of the resume response when the URL has thanks=1). All server text is set with textContent.
 */
export interface LeadCompletePayload {
    mode: string;
    modeLabel: string;
    heading: string;
    confirmation: string;
    summary: {label: string, value: string}[];
    nextSteps: string;
    referenceLabel: string;
    reference: string;
    listingsEnabled: boolean;
    searchUrl: string;
    searchLabel: string;
    backLabel: string;
    newChatLabel: string;
    confirmationOnly: boolean;
    /** The site's real thank-you page for this lead; when set the chat navigates there instead of showing the card. */
    thankYouUrl?: string;
}

function el(tag: string, className: string, text?: string): HTMLElement {
    const e = document.createElement(tag);
    e.className = className;
    if (text) e.textContent = text;
    return e;
}

export function buildLeadCompleteCard(p: LeadCompletePayload, onBack: () => void, onNewChat: (() => void) | null): HTMLElement {
    const card = el("section", "idx-ai-thanks");
    card.setAttribute("role", "status");
    card.setAttribute("aria-live", "polite");

    const head = el("div", "idx-ai-thanks-head");
    const icon = el("span", "idx-ai-thanks-icon", "✓");
    icon.setAttribute("aria-hidden", "true");
    head.appendChild(icon);
    const titles = el("div", "idx-ai-thanks-titles");
    titles.appendChild(el("h3", "idx-ai-thanks-heading", p.heading));
    if (p.modeLabel) titles.appendChild(el("div", "idx-ai-thanks-mode", p.modeLabel));
    head.appendChild(titles);
    card.appendChild(head);

    if (p.summary && p.summary.length) {
        const list = el("dl", "idx-ai-thanks-summary");
        for (const row of p.summary) {
            list.appendChild(el("dt", "idx-ai-thanks-label", row.label));
            list.appendChild(el("dd", "idx-ai-thanks-value", row.value));
        }
        card.appendChild(list);
    }
    if (p.nextSteps) card.appendChild(el("p", "idx-ai-thanks-next", p.nextSteps));
    if (p.reference && p.referenceLabel) {
        const ref = el("p", "idx-ai-thanks-ref");
        ref.appendChild(el("span", "idx-ai-thanks-ref-label", p.referenceLabel + ": "));
        ref.appendChild(el("strong", "idx-ai-thanks-ref-value", p.reference));
        card.appendChild(ref);
    }

    const actions = el("div", "idx-ai-thanks-actions");
    const back = el("button", "idx-ai-thanks-btn idx-ai-thanks-btn-primary", p.backLabel) as HTMLButtonElement;
    back.type = "button";
    back.addEventListener("click", (e) => {
        e.preventDefault();
        onBack();
    });
    actions.appendChild(back);
    if (onNewChat) {
        const fresh = el("button", "idx-ai-thanks-btn", p.newChatLabel) as HTMLButtonElement;
        fresh.type = "button";
        fresh.addEventListener("click", (e) => {
            e.preventDefault();
            onNewChat();
        });
        actions.appendChild(fresh);
    }
    if (p.listingsEnabled && p.searchUrl) {
        const search = el("a", "idx-ai-thanks-btn idx-ai-thanks-search", p.searchLabel) as HTMLAnchorElement;
        search.href = p.searchUrl;
        actions.appendChild(search);
    }
    card.appendChild(actions);
    return card;
}
